<template>
  <div class="fill-height">
    <v-card flat>
      <v-card-title class="py-0 px-0">
        <toolbar-period-selector
          v-model="dataTable.filter.filter_date"
          default-period="today"
          hide-year
        >
          <reports-button
            v-if="userCanManage && (userIsOwner || userIsSuperAdmin)"
            :disable-tooltip="!$vuetify.breakpoint.xsOnly"
            :fab="$vuetify.breakpoint.xsOnly"
            :hide-label="$vuetify.breakpoint.xsOnly"
            :products-report-history="posUserHasComponentPermission('ReportsNotify', 'ACCESS')"
            depressed
            flat
            products-report
            products-report-email
            products-report-pdf
            products-report-print
            @print-products-report="printer => printStatsProducts(printer)"
            @download-pdf-products-report="() => downloadPdfProductsReport()"
            @send-email-products-report="() => onExportButtonClick()"
          />

          <!--
          <print-button
            :fab="$vuetify.breakpoint.xsOnly"
            :hide-label="$vuetify.breakpoint.xsOnly"
            btn-class="ma-1"
            color="primary"
            flat
            menu-offset-y
            @printer-selected="printer => printStatsProducts(printer)"
          />

          <v-btn
            v-if="userCanManage && (userIsOwner || userIsSuperAdmin)"
            :color="$t('Customer.Table.Button.Export.Color')"
            :disabled="!dataTable.filter.filter_date"
            :icon="$vuetify.breakpoint.xsOnly"
            class="elevation-0 ma-1"
            text
            @click="onExportButtonClick"
          >
            <v-icon :left="$vuetify.breakpoint.smAndUp">
              {{ $t('Customer.Table.Button.Export.Icon') }}
            </v-icon>&nbsp;
            <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('Customer.Table.Button.Export.Title').toLocaleUpperCase($i18n.locale) }}</span>
          </v-btn>
          -->

          <v-spacer />

          <filter-search v-model="dataTable.search.terms" />

          <filter-options
            v-model="filtersModel"
            :filters="filterData"
          />
        </toolbar-period-selector>
      </v-card-title>

      <v-divider />

      <v-container
        class="pa-0"
        fluid
      >
        <v-row>
          <v-col cols="12">
            <v-data-table
              :footer-props="dataTable.footerProps"
              :headers="dataTableHeaders"
              :height="dataTable.resize.bodyHeight"
              :items="dataTable.data"
              :loading="dataTable.loading"
              :loading-text="dataTable.text.loadingText"
              :no-data-text="dataTable.text.noDataText"
              :no-results-text="dataTable.text.noResultsText"
              :options.sync="dataTable.options"
              :search="dataTable.search.terms"
              :server-items-length="dataTable.total"
              fixed-header
              item-key="Id"
            >
              <template #[`item.CategoryName`]="{item}">
                <v-chip
                  :color="`#${item.CategoryColor}`"
                  :style="`color: ${getContrastColor(`#${item.CategoryColor || '666666'}`, true, 130)}`"
                  class="px-1"
                  label
                  small
                >
                  {{ item.CategoryName }}
                </v-chip>
              </template>

              <template #[`item.CatalogName`]="{item}">
                <template v-for="catalog in item.Catalogs">
                  <v-chip
                    :key="`catalog-${catalog.Id}`"
                    :color="`#${catalog.Color}`"
                    :style="`color: ${getContrastColor(`#${catalog.Color || '666666'}`, true, 130)}`"
                    class="mr-1 px-1"
                    label
                    small
                  >
                    {{ catalog.Name }}
                  </v-chip>
                </template>
              </template>

              <template #[`item.Name`]="{item}">
                <v-chip
                  class="px-0 mr-1 justify-center"
                  label
                  style="min-width: 40px;"
                  x-small
                >
                  #{{ item.Id }}
                </v-chip>

                {{ item.Name }}
              </template>

              <template #[`item.Quantity`]="{item}">
                {{ item.Quantity }}
              </template>

              <template #[`item.VatRate`]="{item}">
                {{ item.TaxRate }}
              </template>

              <template #[`item.Vat`]="{item}">
                {{ item.SalesVat | currency }}
              </template>

              <template #[`item.Net`]="{item}">
                {{ item.SalesNet | currency }}
              </template>

              <template #[`item.Sales`]="{item}">
                {{ item.Sales | currency }}
              </template>

              <template #foot="{items}">
                <template v-if="!!items.length">
                  <tbody style="height: calc(100% - 180px)" />

                  <template v-if="$vuetify.breakpoint.xsOnly">
                    <tbody class="font-weight-bold text-body-1 grey darken-1 my-2">
                      <tr class="v-data-table__mobile-table-row">
                        <td class="v-data-table__mobile-row">
                          <div class="v-data-table__mobile-row__header">
                            {{ $t('Statistics.Product.Table.Summary.Title') }}
                          </div>
                        </td>

                        <td class="v-data-table__mobile-row">
                          <div class="v-data-table__mobile-row__header">
                            {{ $t('Statistics.Product.Table.Headers.Quantity') }}
                          </div>
                          <div class="v-data-table__mobile-row__cell">
                            {{ sumArrayProperty(items, 'Quantity') }}
                          </div>
                        </td>

                        <td class="v-data-table__mobile-row">
                          <div class="v-data-table__mobile-row__header">
                            {{ $t('Statistics.Product.Table.Headers.Vat') }}
                          </div>
                          <div class="v-data-table__mobile-row__cell">
                            {{ sumArrayProperty(items, 'SalesVat') | currency }}
                          </div>
                        </td>

                        <td class="v-data-table__mobile-row">
                          <div class="v-data-table__mobile-row__header">
                            {{ $t('Statistics.Product.Table.Headers.Net') }}
                          </div>
                          <div class="v-data-table__mobile-row__cell">
                            {{ sumArrayProperty(items, 'SalesNet') | currency }}
                          </div>
                        </td>

                        <td class="v-data-table__mobile-row">
                          <div class="v-data-table__mobile-row__header">
                            {{ $t('Statistics.Product.Table.Headers.Sales') }}
                          </div>
                          <div class="v-data-table__mobile-row__cell">
                            {{ sumArrayProperty(items, 'Sales') | currency }}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </template>

                  <template v-else>
                    <tbody class="font-weight-bold text-body-1 grey darken-1 tbody-sticky">
                      <tr>
                        <td
                          class="text-left"
                          colspan="2"
                        >
                          {{ $t('Statistics.Product.Table.Summary.Title') }}
                        </td>
                        <td />
                        <td class="text-center">
                          {{ sumArrayProperty(items, 'Quantity') }}
                        </td>
                        <td />
                        <td />
                        <td class="text-right">
                          {{ sumArrayProperty(items, 'SalesVat') | currency }}
                        </td>
                        <td class="text-right">
                          {{ sumArrayProperty(items, 'SalesNet') | currency }}
                        </td>
                        <td class="text-right">
                          {{ sumArrayProperty(items, 'Sales') | currency }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </template>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <email-select-dialog
      :visible.sync="emailSelectDialogVisible"
      use-user-email
      @save:dialog="onEmailSelected"
    />
  </div>
</template>

<script>
import FilterSearch from '@/components/common/filter/FilterSearch'
import FilterCommon from '@/mixins/filter/filterCommon'
import DataTable from '@/mixins/data-table/dataTable'
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import ToolbarPeriodSelector from '@/components/common/ToolbarPeriodSelector.vue'
import PrintButton from '@/components/common/print/PrintButton.vue'
import Printer from '@/lib/print/printer.js'
import {getContrastColor} from '@/lib/color/index.js'
import FilterOptions from '@/components/common/filter/FilterOptions.vue'
import {getLocaleForReports, sumArrObjProp} from '@/lib/helper/helper.js'
import EmailSelectDialog from '@/components/common/EmailSelectDialog.vue'
import ReportsButton from '@/components/common/print/ReportsButton.vue'

export default {
  name      : 'ProductStatistics',
  components: {
    ReportsButton,
    EmailSelectDialog,
    FilterOptions,
    PrintButton,
    ToolbarPeriodSelector,
    FilterSearch
  },
  directives: {},
  mixins    : [AppData, Auth, FilterCommon, DataTable],
  data() {
    return {
      selectDateRangeDialogVisible: false,
      emailSelectDialogVisible    : false,
      categories                  : [],
      filtersModel                : {
        lang          : 'Statistics.Product.Table',
        filterCategory: {
          key  : 'filter_category',
          value: null,
          items: []
        }
      },
      dataTable: {
        socketCommand: {
          list: window.SocketCommand.Statistics.Products.All
        },
        filter: {
          filter_date: 'today'
        },
        options: {
          defaultSortBy: 'Quantity',
          sortBy       : ['Quantity'],
          sortDesc     : [true]
        },
        headers: [
          {
            text     : this.$t('Statistics.Product.Table.Headers.CategoryName'),
            value    : 'CategoryName',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Statistics.Product.Table.Headers.CatalogName'),
            value    : 'CatalogName',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Statistics.Product.Table.Headers.Name'),
            value    : 'Name',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Statistics.Product.Table.Headers.Quantity'),
            value    : 'Quantity',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Statistics.Product.Table.Headers.UnitQuantity'),
            value    : 'UnitQuantity',
            align    : 'center',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Statistics.Product.Table.Headers.VatRate'),
            value    : 'VatRate',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Statistics.Product.Table.Headers.Vat'),
            value    : 'Vat',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Statistics.Product.Table.Headers.Net'),
            value    : 'Net',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Statistics.Product.Table.Headers.Sales'),
            value    : 'Sales',
            align    : 'right',
            sortable : true,
            cellClass: 'text-caption'
          }
        ]
      }
    }
  },
  computed: {
    tbodyHeight() {
      return this.$vuetify.breakpoint.xsOnly ? 'auto' : '0'
    },

    categoriesFilterData() {
      return this.categories
    }
  },
  watch: {},
  created() {
    // Init Custom Filter Values
    this.filtersModel.filterCategory.items = this.categoriesFilterData
  },
  mounted() {
    this.$bus.$on(window.SocketCommand.Category.All, this.setCategoriesData)
    this.$bus.$on(window.SocketCommand.Statistics.Products.Export, this.onExportResult)

    window.callAS(window.SocketCommand.Category.All, {
      config    : 0,
      page_limit: -1,
      all       : true
    })
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Category.All, this.setCategoriesData)
    this.$bus.$off(window.SocketCommand.Statistics.Products.Export, this.onExportResult)
  },
  methods: {
    getContrastColor,

    sumArrayProperty(arr, prop) {
      return sumArrObjProp(arr, prop)
    },

    printStatsProducts(printer) {
      if (!printer) return
      const dataTablePayload = this.dataTableRequestData()
      const payload = {
        Period : this.dataTable.filter.filter_date,
        SortBy : dataTablePayload.sort_by,
        OrderBy: dataTablePayload.order_by
      }

      Printer.printStatsProducts(payload.Period, payload.SortBy, payload.OrderBy, printer)
    },

    downloadPdfProductsReport() {
      const dateRange = this.dataTable.filter.filter_date
      const token = window.authorizationToken
      const locale = getLocaleForReports()
      const dataTablePayload = this.dataTableRequestData()
      const sortBy = dataTablePayload.sort_by
      const orderBy = dataTablePayload.order_by
      const url = `${import.meta.env.VITE_API_URL}pdf/stats/products/${token}/${locale}/${dateRange}/${sortBy}/${orderBy}`
      //console.log('downloadPdfProductsReport', url)
      const win = window.open(url, '_blank')
      if (win) win.focus()
    },

    onExportButtonClick() {
      if (!this.dataTable?.filter?.filter_date) return
      this.emailSelectDialogVisible = true
    },
    onEmailSelected(emails) {
      if (!Array.isArray(emails) || !emails?.length) return

      const dataTablePayload = this.dataTableRequestData()
      const payload = {
        Emails  : emails,
        Period  : this.dataTable.filter.filter_date,
        Category: this.filtersModel.filterCategory.value,
        SortBy  : dataTablePayload.sort_by,
        OrderBy : dataTablePayload.order_by
      }

      window.callAS(window.SocketCommand.Statistics.Products.Export, payload)
    },

    onExportResult(response) {
      if (response.status === 'success') {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Statistics.Product.Export.SuccessMessage'),
          type   : 'success',
          icon   : 'check',
          timeout: 5000
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Common.Error.Generic'),
          type   : 'error',
          icon   : 'warning',
          timeout: 3000
        })
      }
    },

    setCategoriesData(data) {
      data = (data?.items || []).map((o) => {
        if (o.Image) {
          o.Avatar = {
            Img: `${this.appConfig.LOCATION_DATA.CdnImagesUrl}${o.Image}`
          }
        } else {
          o.Avatar = {
            Icon : 'category',
            Color: 'grey'
          }
        }
        // Set Active Catalog Name
        o.Description = this.appConfig?.LOCATION_DATA?.Catalogs?.filter(catalog => o.Catalogs.includes(catalog.Id))?.map(o => o.Name)?.join(', ') || ''
        return o
      })

      this.categories = data || []
      this.filtersModel.filterCategory.items = this.categoriesFilterData
    }
  }
}
</script>

<style scoped>
:deep(.v-data-table > .v-data-table__wrapper > table) {
  height: 100%;
}

:deep(.v-data-table > .v-data-table__wrapper > table > tbody),
:deep(.v-data-table > .v-data-table__wrapper > table > thead) {
  height: v-bind('tbodyHeight');
}

.tbody-sticky {
  position: sticky;
  bottom: 0;
  height: 48px;
}
</style>
